import { faCircleNotch, faTimes } from '@fortawesome/pro-light-svg-icons'
import { DateCalendar } from '@mui/x-date-pickers'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '../../App/Components/Button/Button'
import { useAppDispatch } from '../../App/Hooks/reduxHooks'
import { useWindowDimensions } from '../../Common/Hooks/useWindowDimensions'
import { IUserHr } from '../../User/UsersHr/interfaces/IUserHr'
import { getHrUsers } from '../../User/UsersHr/Store/Actions'
import {
  ButtonSpinner,
  SpinnerIcon,
  SpinnerWrapper,
} from '../EditPerson/components/AvatarEdit/style'
import ServiceClient from '../OutOfService/Client/Client'

import Client from './Client/Client'
import {
  Backdrop,
  BodyText,
  BodyWrapper,
  ButtonsWrapper,
  CloseButton,
  DatePickerWrapper,
  DeletePersonDialogWrapper,
  HeaderWrapper,
  Label,
  LabelList,
  LabelListItem,
  LabelListText,
  LabelText,
  ModalWrapper,
  PersonHeader,
  PersonNameText,
  TextWrapper,
} from './style'

type Props = {
  selectedPerson: IUserHr
  setPersonCrud: (removePerson: null) => void
}

export const DeletePerson: React.FC<Props> = ({
  selectedPerson,
  setPersonCrud,
}) => {
  const { t } = useTranslation()
  const { windowWidth } = useWindowDimensions()

  const dispatch = useAppDispatch()
  const [isEditRequestLoading, setIsEditRequestLoading] =
    useState<boolean>(false)
  const [currentDate, setCurrentDate] = useState<moment.Moment | null>(null)
  const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [errorDates, setErrorDates] = useState<string[]>([])
  const [isErrorMessageVisible, setIsErrorMessageVisible] =
    useState<boolean>(false)

  useEffect(() => {
    setCurrentDate(moment())
    setSelectedDate(moment())
  }, [])

  const handleDateChange = (newDate: moment.Moment | null) => {
    setSelectedDate(newDate)
  }

  const deleteOldPerson = () => {
    const selectedMoment = moment(selectedDate, 'YYYY-MM-DD')
    const convertedDate = selectedDate?.format('YYYY-MM-DD')
    setIsEditRequestLoading(true)
    if (currentDate !== null && selectedMoment.isSame(currentDate, 'day')) {
      Client.deletePerson(selectedPerson.uuid)
        .then(response => {
          setIsEditRequestLoading(false)
          if (response.status === 'success') {
            dispatch(getHrUsers())
            setPersonCrud(null)
          }
        })
        .catch(
          (err: {
            response: {
              data: { status: string; message: string; dates?: string[] }
            }
          }) => {
            if (err.response.data.status === 'error') {
              setIsEditRequestLoading(false)
              setIsErrorMessageVisible(true)
              setErrorMessage(err.response.data.message)
              if (err.response.data.dates) {
                setErrorDates(err.response.data.dates)
              }

              setTimeout(() => {
                setIsErrorMessageVisible(false)
                setErrorMessage('')
              }, 4000)
            }
          },
        )
    } else {
      ServiceClient.outOfServicePerson(selectedPerson.uuid, convertedDate)
        .then(response => {
          setIsEditRequestLoading(false)
          if (response.status === 'success') {
            dispatch(getHrUsers())
            setPersonCrud(null)
          }
        })
        .catch(
          (err: {
            response: {
              data: { status: string; message: string; dates?: string[] }
            }
          }) => {
            if (err.response.data.status === 'error') {
              setIsEditRequestLoading(false)
              setIsErrorMessageVisible(true)
              setErrorMessage(err.response.data.message)
              if (err.response.data.dates) {
                setErrorDates(err.response.data.dates)
              }

              setTimeout(() => {
                setIsErrorMessageVisible(false)
                setErrorMessage('')
              }, 4000)
            }
          },
        )
    }
  }

  return (
    <>
      <ModalWrapper>
        <DeletePersonDialogWrapper>
          <HeaderWrapper>
            <PersonHeader>{t('Delete person')}</PersonHeader>
            <CloseButton icon={faTimes} onClick={() => setPersonCrud(null)} />
          </HeaderWrapper>
          <BodyWrapper>
            <TextWrapper>
              <BodyText>{t('You are about to delete user')}</BodyText>
              <PersonNameText>{`${selectedPerson.firstName} ${selectedPerson.lastName}`}</PersonNameText>
            </TextWrapper>
            <DatePickerWrapper>
              <p style={{ fontWeight: 700, textAlign: 'center' }}>
                {t(
                  'Please select the date this employee is going out of service',
                )}
              </p>
              <div>
                <DateCalendar
                  sx={{
                    width: windowWidth < 450 ? '100%' : '320px',
                  }}
                  disablePast
                  defaultValue={currentDate}
                  onChange={newDate => handleDateChange(newDate)}
                />
              </div>
            </DatePickerWrapper>
            <BodyText>{t('Are you sure you want to proceed ?')}</BodyText>
            {isErrorMessageVisible && (
              <>
                <Label color={'error'}>
                  <LabelText>{t(`${errorMessage}`)}</LabelText>
                </Label>

                {errorDates.length > 0 && (
                  <LabelList>
                    <LabelListText>
                      {t('Following appointment dates')} 📅
                    </LabelListText>
                    <ul>
                      {errorDates.map(date => (
                        <LabelListItem key={date}>
                          {moment(date).format('DD/MM/YYYY')}
                        </LabelListItem>
                      ))}
                    </ul>
                  </LabelList>
                )}
              </>
            )}
          </BodyWrapper>
          <ButtonsWrapper>
            <Button
              onClick={() => setPersonCrud(null)}
              color={'rgba(0, 0, 0, 0.3)'}>
              {t('Cancel')}
            </Button>
            {isEditRequestLoading ? (
              <ButtonSpinner>
                <SpinnerWrapper>
                  <SpinnerIcon icon={faCircleNotch} />
                </SpinnerWrapper>
              </ButtonSpinner>
            ) : (
              <Button onClick={deleteOldPerson}>{t('Delete')}</Button>
            )}
          </ButtonsWrapper>
        </DeletePersonDialogWrapper>
      </ModalWrapper>
      <Backdrop onClick={() => setPersonCrud(null)} />
    </>
  )
}
